export const getFileAttachment = (message) => {
  if (message?.attachments && Array.isArray(message.attachments) && message.attachments.length > 0) {
    return message.attachments[0];
  }
  return null;
};

export const isVoiceMessage = (message) => (
  message?.message_type === 'voice_message'
  || getFileAttachment(message)?.kind === 'voice'
);

export const normalizeMessageId = (messageId) => {
  if (messageId == null || messageId === '') {
    return null;
  }
  return String(messageId).toLowerCase();
};

/** Monotonic send order — survives equal / truncated server timestamps. */
let clientSeqCounter = 0;
export const nextClientSeq = () => {
  clientSeqCounter += 1;
  return clientSeqCounter;
};

const messageSortKey = (msg) => {
  const t = new Date(msg?.created_at || msg?.timestamp || 0).getTime();
  const time = Number.isNaN(t) ? 0 : t;
  const seq = Number(msg?.client_seq) || 0;
  return { time, seq };
};

export const compareMessagesByTime = (a, b) => {
  const ka = messageSortKey(a);
  const kb = messageSortKey(b);
  if (ka.time !== kb.time) return ka.time - kb.time;
  if (ka.seq !== kb.seq) return ka.seq - kb.seq;
  return String(a?.id || '').localeCompare(String(b?.id || ''));
};

export const normalizeIncomingMessage = (messageData) => {
  const normalized = { ...messageData };
  if (isVoiceMessage(normalized)) {
    normalized.message_type = 'voice_message';
    normalized.message = '';
    return normalized;
  }
  if (
    normalized.message_type === 'file_message' ||
    (normalized.attachments && normalized.attachments.length > 0)
  ) {
    normalized.message = '';
  }
  return normalized;
};

export const buildSenderDisplayName = (messageData) =>
  messageData?.sender_name || messageData?.sender_connect_id || 'user';

export const buildMessagePreview = (messageData) => {
  if (isVoiceMessage(messageData)) {
    return 'voice message';
  }
  const file = getFileAttachment(messageData);
  if (messageData?.message_type === 'file_message' || file) {
    return file?.filename ? `file: ${file.filename}` : 'sent a file';
  }
  if (messageData?.message) {
    return messageData.message.length > 50
      ? `${messageData.message.substring(0, 50)}...`
      : messageData.message;
  }
  return 'sent a message';
};

const isSameFileMessage = (leftMessage, rightMessage) => {
  const leftFile = getFileAttachment(leftMessage);
  const rightFile = getFileAttachment(rightMessage);
  return (
    leftFile?.url_hash === rightFile?.url_hash ||
    (leftFile?.url && leftFile.url === rightFile?.url)
  );
};

const isSameSender = (leftSenderId, rightSenderId) => (
  String(leftSenderId || '').toLowerCase() === String(rightSenderId || '').toLowerCase()
);

const isBrokenPlain = (text) => (
  typeof text === 'string'
  && (text.startsWith('🔒') || text === 'cannot decrypt')
);

export const preferPlainMessage = (candidate, existing) => {
  if (!existing) return candidate;
  if (!candidate) return existing;
  if (isBrokenPlain(candidate?.message) && !isBrokenPlain(existing?.message)) {
    return existing;
  }
  if (!isBrokenPlain(candidate?.message) && isBrokenPlain(existing?.message)) {
    return candidate;
  }
  return candidate;
};

/** Merge server history with already-rendered live messages; never let 🔒 replace plaintext. */
export const mergeMessageListsPreferPlain = (historyMessages, liveMessages, chatId = null) => {
  const byId = new Map();
  // Prefer live UI first so a history refetch cannot clobber outbound plaintext.
  (liveMessages || []).forEach((msg) => {
    if (chatId != null && msg?.chat_id != null && String(msg.chat_id) !== String(chatId)) {
      return;
    }
    const nid = normalizeMessageId(msg?.id);
    if (nid) {
      byId.set(nid, msg);
      return;
    }
    if (msg?.isOptimistic) {
      byId.set(`optimistic:${msg.created_at || byId.size}:${msg.message || ''}`, msg);
    }
  });
  (historyMessages || []).forEach((msg) => {
    const nid = normalizeMessageId(msg?.id);
    if (!nid) return;
    byId.set(nid, preferPlainMessage(msg, byId.get(nid)));
  });
  return Array.from(byId.values()).sort(compareMessagesByTime);
};

export const mergeIncomingMessage = (prev, messageData, messageId, processedIdsRef) => {
  const normalized = normalizeIncomingMessage(messageData);
  const messageFile = getFileAttachment(normalized);
  const normalizedId = normalizeMessageId(messageId);

  const existingById = normalizedId
    ? prev.find((msg) => normalizeMessageId(msg.id) === normalizedId)
    : null;
  if (existingById) {
    const merged = preferPlainMessage(normalized, existingById);
    return {
      messages: prev.map((msg) => (
        normalizeMessageId(msg.id) === normalizedId ? merged : msg
      )),
      shouldScroll: false,
    };
  }

  const optimisticIndex = prev.findIndex((msg) => {
    if (
      !msg.isOptimistic ||
      !isSameSender(msg.sender_id, normalized.sender_id) ||
      String(msg.chat_id) !== String(normalized.chat_id)
    ) {
      return false;
    }

    if (isVoiceMessage(normalized) || normalized.message_type === 'file_message' || messageFile) {
      if (isVoiceMessage(normalized)) {
        return isVoiceMessage(msg);
      }
      return isSameFileMessage(msg, normalized);
    }

    // Match plaintext, or any pending text bubble when the echo is e2e ciphertext/error.
    return msg.message === normalized.message
      || (normalized.e2e || normalized.e2e_version || (typeof normalized.message === 'string' && normalized.message.startsWith('e2e1.'))
        || (typeof normalized.message === 'string' && normalized.message.startsWith('🔒')));
  });

  if (optimisticIndex !== -1) {
    const prevMsg = prev[optimisticIndex];
    const incomingBroken = typeof normalized.message === 'string'
      && (normalized.message.startsWith('🔒') || normalized.message === 'cannot decrypt');
    const keepPlain = incomingBroken
      && prevMsg.message
      && !String(prevMsg.message).startsWith('🔒');
    // Keep local created_at / client_seq so rapid sends don't jump when the
    // server echoes timestamps truncated to whole seconds.
    const orderFields = {
      created_at: prevMsg.created_at || normalized.created_at,
      client_seq: prevMsg.client_seq ?? normalized.client_seq,
    };
    const merged = keepPlain
      ? {
        ...normalized,
        ...orderFields,
        message: prevMsg.message,
        encrypted: true,
        e2e: true,
        isOptimistic: false,
      }
      : { ...normalized, ...orderFields, isOptimistic: false };
    return {
      messages: prev.map((msg, index) => (index === optimisticIndex ? merged : msg)),
      shouldScroll: true,
    };
  }

  // Dedup only by message id (handled above). Never drop same-text bursts —
  // rapid identical messages are valid and must all render live.
  return { messages: [...prev, normalized], shouldScroll: true };
};

export const shouldProcessMessageId = (messageId, processedIdsRef) => {
  const normalizedId = normalizeMessageId(messageId);
  if (!normalizedId) {
    return false;
  }
  if (processedIdsRef.current.has(normalizedId)) {
    return false;
  }
  processedIdsRef.current.add(normalizedId);
  return true;
};
